import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Profile.css';
import { API_URL } from '../../../../../config';

const IconUpload = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('zTaskAuthToken')}`,
});

const Profile = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [employee, setEmployee] = useState(null);
    const [banks, setBanks] = useState([]);
    const [docs, setDocs] = useState([]);
    const [bankForm, setBankForm] = useState({
        account_number: '',
        ifsc_code: '',
        bank_name: '',
        branch_name: '',
        contact_number: '',
    });
    const fileRef = useRef(null);

    const loadProfile = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const meRes = await fetch(`${API_URL}/auth/me`, { headers: authHeaders() });
            if (!meRes.ok) throw new Error('Unable to load session');
            const me = await meRes.json();
            const userId = me.user?.id;
            if (!userId) throw new Error('User id missing');

            const [empRes, bankRes, docsRes] = await Promise.all([
                fetch(`${API_URL}/hr/employees/${userId}`, { headers: authHeaders() }),
                fetch(`${API_URL.replace(/\/api\/?$/, '')}/api/get-column/common_master/bank_name`, { headers: authHeaders() }),
                fetch(`${API_URL}/documents/${userId}`, { headers: authHeaders() }).catch(() => null),
            ]);

            if (!empRes.ok) throw new Error('Unable to load profile');
            const emp = await empRes.json();
            setEmployee(emp);
            setBankForm({
                account_number: emp.acno || '',
                ifsc_code: emp.ifsc_code || '',
                bank_name: emp.bank_name || emp.bankname_id || '',
                branch_name: emp.branch || '',
                contact_number: emp.contact_details || '',
            });

            if (bankRes.ok) {
                const bj = await bankRes.json();
                setBanks(bj.data || bj || []);
            }
            if (docsRes && docsRes.ok) {
                const dj = await docsRes.json();
                setDocs(dj.data || dj || []);
            }
        } catch (e) {
            setError(e.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    const saveBank = async (e) => {
        e.preventDefault();
        if (!employee?.emp_code) return;
        setSaving(true);
        setMessage(null);
        try {
            const res = await fetch(`${API_URL}/hr/employees/bank-details`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    emp_code: employee.emp_code,
                    ...bankForm,
                }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.error || 'Save failed');
            setMessage('Profile updated');
            loadProfile();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const uploadDoc = async (file) => {
        if (!file || !employee?.id) return;
        const fd = new FormData();
        fd.append('user_id', employee.id);
        fd.append('general', file);
        try {
            const res = await fetch(`${API_URL}/documents/upload`, {
                method: 'POST',
                headers: authHeaders(),
                body: fd,
            });
            if (!res.ok) {
                const b = await res.json().catch(() => ({}));
                throw new Error(b.error || 'Upload failed');
            }
            setMessage('Document uploaded');
            loadProfile();
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) {
        return <div className="profile-page"><p>Loading profile…</p></div>;
    }

    if (error && !employee) {
        return <div className="profile-page"><p style={{ color: '#b91c1c' }}>{error}</p></div>;
    }

    return (
        <div className="profile-page">
            <div className="profile-header">
                <div className="profile-avatar">{(employee.first_name || 'U')[0]}</div>
                <div>
                    <h1>{employee.first_name} {employee.last_name}</h1>
                    <p>{employee.email}</p>
                </div>
            </div>

            {message && <div className="profile-banner success">{message}</div>}
            {error && <div className="profile-banner error">{error}</div>}

            <section className="profile-section">
                <h2>Employment</h2>
                <div className="profile-grid readonly">
                    <div><label>Employee code</label><div>{employee.emp_code}</div></div>
                    <div><label>Role</label><div>{employee.role_name || '—'}</div></div>
                    <div><label>Department</label><div>{employee.department_name || '—'}</div></div>
                    <div><label>Designation</label><div>{employee.designation_name || '—'}</div></div>
                    <div><label>Date of joining</label><div>{employee.date_of_joining || '—'}</div></div>
                    <div><label>Manager</label><div>{employee.reporting_manager_name || '—'}</div></div>
                </div>
            </section>

            <section className="profile-section">
                <h2>Contact & bank</h2>
                <form onSubmit={saveBank} className="profile-grid">
                    <label>Contact number
                        <input value={bankForm.contact_number} onChange={(e) => setBankForm({ ...bankForm, contact_number: e.target.value })} required />
                    </label>
                    <label>Account number
                        <input value={bankForm.account_number} onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })} required />
                    </label>
                    <label>IFSC
                        <input value={bankForm.ifsc_code} onChange={(e) => setBankForm({ ...bankForm, ifsc_code: e.target.value })} required />
                    </label>
                    <label>Branch
                        <input value={bankForm.branch_name} onChange={(e) => setBankForm({ ...bankForm, branch_name: e.target.value })} required />
                    </label>
                    <label>Bank
                        <select value={bankForm.bank_name} onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })} required>
                            <option value="">Select bank</option>
                            {banks.map((b) => (
                                <option key={b.id} value={b.bank_name || b.value}>{b.bank_name || b.value}</option>
                            ))}
                        </select>
                    </label>
                    <div className="profile-actions">
                        <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save details'}</button>
                    </div>
                </form>
            </section>

            <section className="profile-section">
                <h2>Documents</h2>
                <ul className="profile-docs">
                    {(Array.isArray(docs) ? docs : []).map((d) => (
                        <li key={d.id || d.file_url}>
                            <span>{d.document_type || d.file_name || 'Document'}</span>
                            {d.file_url && <a href={d.file_url} target="_blank" rel="noreferrer">Open</a>}
                        </li>
                    ))}
                    {(!docs || docs.length === 0) && <li>No documents uploaded yet.</li>}
                </ul>
                <button type="button" className="browse-button" onClick={() => fileRef.current?.click()}>
                    <IconUpload /> Upload document
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadDoc(f);
                        e.target.value = '';
                    }}
                />
            </section>
        </div>
    );
};

export default Profile;
